import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@components/shadcn-ui/button";
import { Input } from "@components/shadcn-ui/input";
import { Label } from "@components/shadcn-ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/shadcn-ui/select";
import { useToast } from "@customHooks/useToast";
import { Loader2 } from "lucide-react";
import { AddressForm } from "./AddressForm";
import { addressService, AddressResponse } from "../service/addressService";

export interface CoApplicantFormData {
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
  };
  kycDetails?: {
    panNumber?: string;
  };
  addressId: string[] | string;
}

interface CoApplicantFormProps {
  applicationId: string;
  leadId: string;
  onCoApplicantAdded: (coApplicant: CoApplicantFormData) => void;
  onCancel: () => void;
  initialData?: CoApplicantFormData;
  isEditMode?: boolean;
  existingPhoneNumbers: string[];
  leadPhoneNumber: string;
  applicantPanNumber: string;
}

export const CoApplicantForm: React.FC<CoApplicantFormProps> = ({
  applicationId,
  leadId,
  onCoApplicantAdded,
  onCancel,
  initialData,
  isEditMode = false,
  existingPhoneNumbers,
  leadPhoneNumber,
  applicantPanNumber,
}) => {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CoApplicantFormData>({
    defaultValues: initialData || {
      relationToApplicant: "",
      personalDetails: {
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
      },
      kycDetails: {
        panNumber: "",
      },
      addressId: [],
    },
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressIds, setAddressIds] = useState<string[]>(
    Array.isArray(initialData?.addressId) 
      ? initialData?.addressId as string[] 
      : initialData?.addressId ? [initialData.addressId as string] : []
  );
  const { toast } = useToast();
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressData, setAddressData] = useState<AddressResponse | null>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);
  
  // Watch phone number and PAN for validation purposes
  // These are used in the validation functions
  const phoneNumber = watch("personalDetails.phoneNumber");
  const panNumber = watch("kycDetails.panNumber");

  // Function to handle editing an address
  const handleEditAddress = async (index: number) => {
    try {
      setIsLoadingAddress(true);
      const addressId = addressIds[index];
      const address = await addressService.getAddressById(applicationId, addressId);
      setAddressData(address);
      setEditingAddressIndex(index);
      setShowAddressForm(true);
    } catch (error) {
      console.error("Error fetching address data for edit:", error);
      toast({
        variant: "destructive",
        description: "Failed to load address data for editing",
      });
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Function to handle adding a new address
  const handleAddNewAddress = () => {
    setAddressData(null);
    setEditingAddressIndex(null);
    setShowAddressForm(true);
  };

  // Load address data when editing a co-applicant
  useEffect(() => {
    const fetchAddressData = async () => {
      if (isEditMode && initialData?.addressId) {
        setIsLoadingAddress(true);
        try {
          // Handle single address ID or array of address IDs
          const addressIdToFetch = Array.isArray(initialData.addressId) 
            ? initialData.addressId[0] 
            : initialData.addressId;
            
          if (addressIdToFetch) {
            const address = await addressService.getAddressById(applicationId, addressIdToFetch);
            setAddressData(address);
          }
        } catch (error) {
          console.error("Error fetching address data:", error);
          toast({
            variant: "destructive",
            description: "Failed to load address data",
          });
        } finally {
          setIsLoadingAddress(false);
        }
      }
    };

    fetchAddressData();
  }, [initialData?.addressId, isEditMode, toast, applicationId]);

  const handleAddressAdded = (address: AddressResponse) => {
    if (editingAddressIndex !== null) {
      // Update existing address
      const newAddressIds = [...addressIds];
      newAddressIds[editingAddressIndex] = address.id;
      setAddressIds(newAddressIds);
      setEditingAddressIndex(null);
    } else {
      // Add new address
      setAddressIds(prev => [...prev, address.id]);
    }
    setAddressData(null); // Clear address data to ensure new form on next add
    setShowAddressForm(false);
  };

  const validatePhoneNumber = (value?: string) => {
    if (!value) return true; // Phone number is optional for co-applicant
    
    // Check if phone number is the same as lead's phone number
    if (value === leadPhoneNumber) {
      return "Co-applicant phone number cannot be the same as the applicant's phone number";
    }
    
    // Check if phone number is already used in another reference
    if (existingPhoneNumbers.includes(value)) {
      return "This phone number is already used for a reference";
    }
    
    return true;
  };

  const validatePanNumber = (value?: string) => {
    if (!value) return true; // PAN is optional for co-applicant
    
    // Check if PAN is the same as applicant's PAN
    if (value === applicantPanNumber) {
      return "Co-applicant PAN number cannot be the same as the applicant's PAN number";
    }
    
    return true;
  };

  const onSubmit = async (data: CoApplicantFormData) => {
    if (addressIds.length === 0) {
      toast({
        variant: "destructive",
        description: "Please add at least one address for the co-applicant",
      });
      return;
    }

    // Validate phone number if provided
    if (data.personalDetails.phoneNumber) {
      const phoneValidation = validatePhoneNumber(data.personalDetails.phoneNumber);
      if (phoneValidation !== true) {
        toast({
          variant: "destructive",
          description: phoneValidation,
        });
        return;
      }
    }

    // Validate PAN number if provided
    if (data.kycDetails?.panNumber) {
      const panValidation = validatePanNumber(data.kycDetails.panNumber);
      if (panValidation !== true) {
        toast({
          variant: "destructive",
          description: panValidation,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Create the co-applicant object with the address IDs
      const coApplicantData: CoApplicantFormData = {
        ...data,
        addressId: addressIds,
      };
      
      onCoApplicantAdded(coApplicantData);
      toast({
        description: `Co-applicant ${isEditMode ? "updated" : "added"} successfully`,
      });
    } catch (error) {
      console.error("Error saving co-applicant:", error);
      toast({
        variant: "destructive",
        description: `Failed to ${isEditMode ? "update" : "add"} co-applicant`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-2 max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-lg font-medium mb-4">
          {isEditMode ? "Edit Co-Applicant" : "Add Co-Applicant"}
        </h3>
        
        <form onSubmit={(e) => {
          e.preventDefault(); // Prevent default form submission
          handleSubmit(onSubmit)(e); // Use react-hook-form's handleSubmit
        }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="relationToApplicant">Relation to Applicant*</Label>
              <Select
                defaultValue={initialData?.relationToApplicant || ""}
                onValueChange={(value) => setValue("relationToApplicant", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spouse">Spouse</SelectItem>
                  <SelectItem value="Parent">Parent</SelectItem>
                  <SelectItem value="Child">Child</SelectItem>
                  <SelectItem value="Sibling">Sibling</SelectItem>
                  <SelectItem value="Business Partner">Business Partner</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.relationToApplicant && (
                <p className="text-red-500 text-sm">{errors.relationToApplicant.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.firstName">First Name*</Label>
              <Input
                id="personalDetails.firstName"
                {...register("personalDetails.firstName", { required: "First name is required" })}
              />
              {errors.personalDetails?.firstName && (
                <p className="text-red-500 text-sm">{errors.personalDetails.firstName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.lastName">Last Name*</Label>
              <Input
                id="personalDetails.lastName"
                {...register("personalDetails.lastName", { required: "Last name is required" })}
              />
              {errors.personalDetails?.lastName && (
                <p className="text-red-500 text-sm">{errors.personalDetails.lastName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="personalDetails.phoneNumber">Phone Number</Label>
              <Input
                id="personalDetails.phoneNumber"
                {...register("personalDetails.phoneNumber", {
                  pattern: {
                    value: /^(?!0)\d{10}$/,
                    message: "Phone number must be valid with 10 digits and cannot start with 0",
                  },
                })}
              />
              {errors.personalDetails?.phoneNumber && (
                <p className="text-red-500 text-sm">{errors.personalDetails.phoneNumber.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="personalDetails.email">Email</Label>
              <Input
                id="personalDetails.email"
                {...register("personalDetails.email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
              {errors.personalDetails?.email && (
                <p className="text-red-500 text-sm">{errors.personalDetails.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="kycDetails.panNumber">PAN Number</Label>
              <Input
                id="kycDetails.panNumber"
                {...register("kycDetails.panNumber", {
                  pattern: {
                    value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                    message: "Enter a valid PAN number (e.g., ABCDE1234F)",
                  },
                })}
              />
              {errors.kycDetails?.panNumber && (
                <p className="text-red-500 text-sm">{errors.kycDetails.panNumber.message}</p>
              )}
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-md font-medium mb-2">Co-Applicant Addresses</h4>
            {isLoadingAddress ? (
              <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Loading addresses...</span>
              </div>
            ) : addressIds.length > 0 && !showAddressForm ? (
              <div>
                <div className="bg-gray-50 p-4 rounded-md mb-3">
                  <h5 className="font-medium mb-2">Added Addresses ({addressIds.length})</h5>
                  <ul className="list-disc pl-5 mb-3">
                    {addressIds.map((id, index) => (
                      <li key={id} className="mb-1 flex items-center justify-between">
                        <span>
                          Address #{index + 1}: <span className="text-xs text-gray-500">{id}</span>
                        </span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-blue-600"
                          onClick={() => handleEditAddress(index)}
                        >
                          Edit
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={handleAddNewAddress}
                  >
                    Add Another Address
                  </Button>
                </div>
              </div>
            ) : showAddressForm ? (
              <AddressForm
                applicationId={applicationId}
                leadId={leadId}
                overrideAddressTypes={["CO_APPLICANT_CURRENT", "CO_APPLICANT_PERMANENT"]}
                onAddressAdded={handleAddressAdded}
                onCancel={() => {
                  if (addressIds.length > 0) {
                    setShowAddressForm(false);
                    setEditingAddressIndex(null);
                    setAddressData(null);
                  } else {
                    onCancel();
                  }
                }}
                initialData={addressData || undefined}
                isEditMode={!!addressData}
              />
            ) : (
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600">Please add at least one address for the co-applicant</p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={handleAddNewAddress}
                >
                  Add Address
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              type="button" 
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? "Update" : "Add"} Co-Applicant
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
